// Body of the Oscar order-pending ("order error") view: the order-error copy
// block, the "ORDER DETAILS" content title, and the flat product-line summary
// + price breakdown. Most copy arrives pre-localized as string props
// (server-side gettext) so fr_CA is preserved; the "Shop Now" button label
// and the line-row group aria-label are client-side ttag strings.
// The trailing .thank-you-esig div renders empty: the
// `customer_service_settings` guard around the chat/phone block referenced an
// undefined variable, so that block never renders in production (preserved
// as-is).
import { Immutable } from "immer";
import React from "react";
import { FORM_FIELD_SUBMIT_CLASS_NAME } from "tsi-common-react/src/forms/classNames";
import { t } from "ttag";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type ISummary = NonNullable<
    templates.OrderPendingDocumentTemplate["order_summary"]
>;

const LineRow = (
    props: Immutable<{ line: ISummary["lines"][number] }>,
): React.JSX.Element => {
    const { line } = props;
    return (
        <div role="group" aria-label={t`product`}>
            <div className="section__left products__results">
                <div className="product__image">
                    <a href={line.product_url} title={line.link_title}>
                        {/* No image element renders for a blank url, so a
                            product-less or image-less line shows an empty
                            link -- never a broken-image icon. */}
                        {line.image_url && (
                            <img
                                src={line.image_url}
                                alt={line.image_alt}
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </a>
                </div>
                <div className="product__details">
                    <p className="product__details__copy">
                        {line.title}
                        <br />
                        {line.attribute_summary}
                        <br />
                        {line.quantity_label} {line.quantity}
                        <br />
                        {line.strike_price ? (
                            <>
                                <span className="product__details__copy--strike">
                                    {line.strike_price}
                                </span>{" "}
                                <span className="product__details__copy--razzmatazz">
                                    {line.price}
                                </span>
                            </>
                        ) : (
                            line.price
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

const Totals = (
    props: Immutable<{ totals: ISummary["totals"] }>,
): React.JSX.Element => {
    const { totals } = props;
    return (
        <div className="thank-you-details details">
            <div className="thank-you-details__columns">
                <div className="thank-you-details__breakdown thank-you-details__column">
                    <div className="thank-you-details__breakdown-row">
                        <div className="thank-you-details__breakdown-title">
                            {totals.subtotal_label}
                        </div>
                        <div className="thank-you-details__breakdown-number">
                            {totals.subtotal}
                        </div>
                    </div>
                    <div className="thank-you-details__breakdown-row">
                        <div className="thank-you-details__breakdown-title">
                            {totals.shipping_label}
                        </div>
                        <div className="thank-you-details__breakdown-number">
                            {totals.shipping}
                        </div>
                    </div>
                    {totals.is_tax_known ? (
                        totals.tax_rows.map((row, i) => (
                            <div
                                key={i}
                                className="thank-you-details__breakdown-row"
                            >
                                <div className="thank-you-details__breakdown-title">
                                    {row.name}
                                </div>
                                <div className="thank-you-details__breakdown-number">
                                    {row.amount}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="thank-you-details__breakdown-row">
                            <div className="thank-you-details__breakdown-title">
                                {totals.tax_label}
                            </div>
                            <div className="thank-you-details__breakdown-number">
                                {totals.tax_pending_message}
                            </div>
                        </div>
                    )}
                    {/* The discount row div always renders, leaving an empty
                        row for undiscounted orders. */}
                    <div className="thank-you-details__breakdown-row">
                        {totals.discount && (
                            <>
                                <div className="thank-you-details__breakdown-title">
                                    {totals.discount_label}
                                </div>
                                <div className="thank-you-details__breakdown-number thank-you-details__breakdown-number--cerise">
                                    {totals.discount}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="thank-you-details__breakdown-row">
                        <div className="thank-you-details__breakdown-title">
                            {totals.total_label}
                        </div>
                        <div className="thank-you-details__breakdown-number">
                            {totals.total}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

type IProps = Immutable<templates.OrderPendingDocumentTemplate>;

export const OrderPendingBody = (props: IProps): React.JSX.Element => {
    const summary = props.order_summary;
    return (
        <div className="thank-you-container l-capped-width u-margin-bottom--xl u-full-width-title">
            <div className="order-error">
                <div className="intro">
                    <h1 className="order-error__intro-title">
                        {props.intro_title}
                    </h1>
                    <div className="order-error__intro-copy">
                        <p>
                            {props.intro_line_1}
                            <br />
                            {props.intro_line_2}
                            <br />
                            {props.intro_line_3}
                        </p>
                        <ol>
                            {props.steps.map((step, i) => (
                                <li key={i}>{step}</li>
                            ))}
                        </ol>
                        <p>{props.reassurance}</p>
                    </div>
                    <div>
                        <p className="order-error__intro-copy--bold">
                            {props.contact_line}
                            <br />
                            {props.hours_line}
                        </p>
                    </div>
                    <div className="order-error__continue">
                        <a href="/">
                            <div
                                className={concatClassNames([
                                    "button",
                                    FORM_FIELD_SUBMIT_CLASS_NAME,
                                ])}
                            >
                                {t`Shop Now`}
                            </div>
                        </a>
                    </div>
                </div>

                {summary && (
                    <div className="thank-you content-title">
                        <h1 className="content-title__headline">
                            {summary.heading}
                        </h1>
                        <span className="content-title__info">
                            {summary.need_help_line}
                        </span>
                    </div>
                )}
            </div>

            {summary && (
                <div className="thank-you-summary">
                    <div className="section products">
                        {summary.lines.map((line, i) => (
                            <LineRow key={i} line={line} />
                        ))}
                    </div>
                    <Totals totals={summary.totals} />
                </div>
            )}

            <div className="thank-you-esig" />
        </div>
    );
};
